import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import api, { formatApiError } from "../lib/api";
import { saveOrderSnapshot } from "../lib/orderSnapshot";
import SEO from "../components/SEO";
import PhoneAuth from "../components/PhoneAuth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useCart();
  const { user, refresh, loading: authLoading } = useAuth();
  const { checkout: cfg } = useTheme();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [coupon, setCoupon] = useState("");
  const [quote, setQuote] = useState(null);
  const [couponMsg, setCouponMsg] = useState("");
  const [couponsActive, setCouponsActive] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}).finally(() => setSettingsLoaded(true));
    if (user) {
      setForm((f) => {
        const saved = user.addresses || [];
        const def = saved.find((a) => a.default) || saved[0] || {};
        return { ...f, name: user.name || "", email: user.email || "", phone: user.phone || "", ...def };
      });
    }
  }, [user]);

  useEffect(() => {
    // Pay Online is the default whenever it's enabled
    if (cfg.online_enabled) setPaymentMethod("razorpay");
    else if (cfg.cod_enabled) setPaymentMethod("cod");
    if (cfg.coupons_enabled) {
      api.get("/coupons/active").then((r) => setCouponsActive(!!r.data.active)).catch(() => {});
    }
  }, [cfg]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (cart.length === 0) return (
    <div className="text-center py-32" data-testid="checkout-empty">
      <p className="font-serif text-2xl">Your cart is empty</p>
      <Link to="/shop" className="small-caps text-gold border-b border-gold mt-4 inline-block">Back to Shop</Link>
    </div>
  );

  const buildSnapshot = (orderNumber, accountCreated = false, accountLinked = false) => saveOrderSnapshot(orderNumber, {
    order_number: orderNumber,
    account_created: accountCreated,
    account_linked: accountLinked,
    created_at: new Date().toISOString(),
    customer: { name: form.name, email: form.email, phone: form.phone },
    address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, pincode: form.pincode },
    items: cart.map((i) => ({ name: i.product.name, qty: i.qty, price: i.product.sale_price || i.product.price, image: i.product.images?.[0] || "" })),
    subtotal: cartTotal,
    discount: quote?.discount || 0,
    cod_fee: paymentMethod === "cod" ? 50 : 0,
    coupon_code: quote?.campaign && coupon.trim() ? coupon.trim().toUpperCase() : "",
    total: (quote ? quote.total : cartTotal) + (paymentMethod === "cod" ? 50 : 0),
    payment_method: paymentMethod,
  });

  const openRazorpay = async (orderData) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      toast.error("Could not load payment gateway. Please try again.");
      return;
    }
    const rzp = new window.Razorpay({
      key: orderData.razorpay.key_id,
      amount: orderData.razorpay.amount,
      currency: orderData.razorpay.currency,
      name: "Humaira Crafts",
      description: `Order ${orderData.order_number}`,
      order_id: orderData.razorpay.order_id,
      prefill: { name: form.name, email: form.email, contact: form.phone },
      theme: { color: "#111111" },
      handler: async (resp) => {
        try {
          await api.post("/orders/verify-payment", {
            order_number: orderData.order_number,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          clearCart();
          buildSnapshot(orderData.order_number, orderData.account_created, orderData.account_linked);
          if (orderData.account_created) await refresh();
          navigate(`/order-success/${orderData.order_number}`);
        } catch (e) {
          toast.error(formatApiError(e));
        }
      },
    });
    rzp.open();
  };

  const applyCoupon = async () => {
    setCouponMsg("");
    try {
      const { data } = await api.post("/cart/quote", {
        items: cart.map((i) => ({ product_id: i.product.id, qty: i.qty })),
        coupon_code: coupon.trim(),
        email: form.email || "guest@checkout",
      });
      setQuote(data);
      if (coupon.trim() && !data.campaign) setCouponMsg("This coupon is not valid for your cart.");
      else if (data.campaign) setCouponMsg(`"${data.campaign.name}" applied — you save ₹${data.discount.toLocaleString("en-IN")}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        customer: { name: form.name, email: form.email, phone: form.phone },
        address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, pincode: form.pincode },
        items: cart.map((i) => ({ product_id: i.product.id, qty: i.qty })),
        payment_method: paymentMethod,
        coupon_code: coupon.trim(),
      });
      if (paymentMethod === "razorpay") {
        await openRazorpay(data);
      } else {
        clearCart();
        buildSnapshot(data.order_number, data.account_created, data.account_linked);
        if (data.account_created) await refresh();
        navigate(`/order-success/${data.order_number}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const ready = !authLoading && settingsLoaded;
  // Step 1 for every shopper: verify mobile via OTP. Payment & address unlock after.
  const verified = !!user;
  const codFee = paymentMethod === "cod" ? 50 : 0;
  const orderSubtotal = quote ? quote.total : cartTotal;
  const orderTotal = orderSubtotal + codFee;

  const fieldLabel = "block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5";
  const fieldInput = "bg-white h-12 text-base md:text-sm border-black/15 focus-visible:border-gold";

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="checkout-page">
      <SEO title="Checkout" description="Secure checkout — Humaira Crafts" />
      <p className="small-caps text-gold mb-2">Secure Checkout</p>
      <h1 className="text-4xl tracking-tighter font-semibold mb-10">Almost Yours</h1>
      {/* Step 01 — verify mobile. Mounted once for guests and only CSS-hidden
          after success, so the invisible reCAPTCHA is never torn down mid-flow. */}
      {ready && (
        <div className={verified ? "hidden" : ""} data-testid="checkout-verify-step">
          <div className="bg-white border border-black/10 border-t-2 border-t-gold p-6 sm:p-10 max-w-xl mx-auto">
            <p className="small-caps text-gold mb-3 flex items-center gap-3"><span className="font-serif text-lg text-[#111]">01</span> Verify Mobile</p>
            <p className="font-serif text-2xl mb-2">One quick step before checkout</p>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              We&apos;ll send a one-time SMS code to confirm it&apos;s you. Payment options and address details unlock right after.
            </p>
            <PhoneAuth testidPrefix="checkout-auth" onSuccess={() => toast.success("Mobile verified — continue to payment & address")} />
            <div className="border-t border-black/10 mt-6 pt-4 flex justify-between text-sm" data-testid="verify-step-summary">
              <span className="text-muted-foreground">{cart.length} item{cart.length > 1 ? "s" : ""} in your bag</span>
              <span className="font-medium">₹{cartTotal.toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>
      )}

      {/* Step 02 onward — visible only after OTP verification */}
      {ready && verified && (
      <form onSubmit={placeOrder} className="grid md:grid-cols-[1fr_380px] gap-12" data-testid="checkout-form">
        <div className="flex flex-col gap-10">
          <div>
            <p className="small-caps text-gold mb-5 flex items-center gap-3"><span className="font-serif text-lg text-[#111]">02</span> Contact</p>
            {user && (
              <p className="text-xs text-muted-foreground mb-4 flex items-center gap-2" data-testid="verified-phone-note">
                Verified mobile: <span className="text-foreground font-medium">{user.phone || form.phone}</span>
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className={fieldLabel}>Full Name *</Label>
                <Input required placeholder="e.g. Ayesha Khan" value={form.name} onChange={set("name")} className={fieldInput} data-testid="checkout-name" />
              </div>
              <div>
                <Label className={fieldLabel}>Email Address *</Label>
                <Input required type="email" placeholder="you@example.com" value={form.email} onChange={set("email")} className={fieldInput} data-testid="checkout-email" />
              </div>
              <div className="md:col-span-2">
                <Label className={fieldLabel}>Mobile Number *</Label>
                <Input required placeholder="10-digit mobile" value={form.phone} onChange={set("phone")} readOnly={!!user?.phone} className={`${fieldInput} ${user?.phone ? "opacity-70 cursor-not-allowed" : ""}`} data-testid="checkout-phone" title={user?.phone ? "Verified mobile number" : ""} />
              </div>
            </div>
          </div>
          <div>
            <p className="small-caps text-gold mb-5 flex items-center gap-3"><span className="font-serif text-lg text-[#111]">03</span> Delivery Address</p>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label className={fieldLabel}>House No, Building, Apartment *</Label>
                <Input required placeholder="e.g. H.No 42, Silver Oak Apartments" value={form.line1} onChange={set("line1")} className={fieldInput} data-testid="checkout-address1" />
              </div>
              <div className="md:col-span-2">
                <Label className={fieldLabel}>Area, Street, Sector</Label>
                <Input placeholder="e.g. MG Road, Sector 14" value={form.line2} onChange={set("line2")} className={fieldInput} data-testid="checkout-address2" />
              </div>
              <div>
                <Label className={fieldLabel}>City *</Label>
                <Input required placeholder="Delhi" value={form.city} onChange={set("city")} className={fieldInput} data-testid="checkout-city" />
              </div>
              <div>
                <Label className={fieldLabel}>State *</Label>
                <Input required placeholder="Delhi" value={form.state} onChange={set("state")} className={fieldInput} data-testid="checkout-state" />
              </div>
              <div>
                <Label className={fieldLabel}>Pincode *</Label>
                <Input required inputMode="numeric" placeholder="110001" value={form.pincode} onChange={set("pincode")} className={fieldInput} data-testid="checkout-pincode" />
              </div>
            </div>
          </div>
          {couponsActive && (
          <div>
            <p className="small-caps text-gold mb-5 flex items-center gap-3"><span className="font-serif text-lg text-[#111]">04</span> Coupon Code</p>
            <div className="flex gap-2">
              <Input placeholder="Enter coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} className={fieldInput} data-testid="coupon-input" />
              <button type="button" onClick={applyCoupon} className="border border-[#111] px-6 small-caps hover:bg-[#111] hover:text-[#FAF9F6] transition-colors duration-300" data-testid="apply-coupon-button">
                Apply
              </button>
            </div>
            {couponMsg && <p className={`text-xs mt-2 ${quote?.campaign ? "text-green-800" : "text-destructive"}`} data-testid="coupon-message">{couponMsg}</p>}
          </div>
          )}
          <div>
            <p className="small-caps text-gold mb-5 flex items-center gap-3"><span className="font-serif text-lg text-[#111]">{couponsActive ? "05" : "04"}</span> Payment Method</p>
            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="flex flex-col gap-3" data-testid="payment-methods">
              {cfg.online_enabled && (
              <label className={`flex items-start gap-3 border p-5 cursor-pointer transition-colors duration-300 ${paymentMethod === "razorpay" ? "border-gold bg-gold-light/30" : "border-black/10 bg-white"}`} data-testid="payment-razorpay">
                <RadioGroupItem value="razorpay" className="mt-1" />
                <span>
                  <span className="text-sm font-medium block">Pay Online — UPI, Cards, Net Banking, Wallets</span>
                  <span className="text-xs text-muted-foreground">Secure payment via Razorpay · recommended</span>
                </span>
              </label>
              )}
              {cfg.cod_enabled && (
              <label className={`flex items-start gap-3 border p-5 cursor-pointer transition-colors duration-300 ${paymentMethod === "cod" ? "border-gold bg-gold-light/30" : "border-black/10 bg-white"}`} data-testid="payment-cod">
                <RadioGroupItem value="cod" className="mt-1" />
                <span>
                  <span className="text-sm font-medium block">Cash on Delivery</span>
                  <span className="text-xs text-muted-foreground">Pay when your order arrives · + ₹50 COD handling fee</span>
                </span>
              </label>
              )}
              {!cfg.online_enabled && !cfg.cod_enabled && (
                <p className="text-sm text-muted-foreground" data-testid="no-payment-methods">No payment methods are currently available. Please contact us.</p>
              )}
            </RadioGroup>
            {paymentMethod === "cod" && cfg.cod_enabled && (
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-2" data-testid="cod-fee-note">
                <span className="inline-block w-1 h-1 bg-gold rounded-full" /> ₹50 COD fee added to your order total
              </p>
            )}
          </div>
        </div>

        <div className="bg-white border border-black/10 border-t-2 border-t-gold p-8 self-start sticky top-32" data-testid="order-summary">
          <p className="font-serif text-2xl mb-6">Your Order</p>
          <div className="flex flex-col gap-3 max-h-56 overflow-y-auto">
            {cart.map((i) => (
              <div key={i.product.id} className="flex justify-between text-sm gap-4">
                <span className="text-muted-foreground">{i.product.name} × {i.qty}</span>
                <span>₹{((i.product.sale_price || i.product.price) * i.qty).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-black/10 mt-4 pt-4 flex justify-between text-sm">
            <span>Subtotal</span><span>₹{(quote ? quote.subtotal : cartTotal).toLocaleString("en-IN")}</span>
          </div>
          {quote?.discount > 0 && (
            <div className="flex justify-between text-sm py-1 text-green-800">
              <span>Discount</span><span>− ₹{quote.discount.toLocaleString("en-IN")}</span>
            </div>
          )}
          {codFee > 0 && (
            <div className="flex justify-between text-sm py-1" data-testid="cod-fee-row">
              <span>COD handling fee</span><span>₹{codFee.toLocaleString("en-IN")}</span>
            </div>
          )}
          <div className="flex justify-between text-sm py-1">
            <span>Shipping</span><span className="text-xs text-muted-foreground text-right max-w-[180px]">{settings?.shipping_message || "Confirmed after order"}</span>
          </div>
          <div className="flex justify-between font-semibold text-lg py-5 border-t border-black/10 mt-3">
            <span>Total</span><span data-testid="checkout-total">₹{orderTotal.toLocaleString("en-IN")}</span>
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-[#111] text-[#FAF9F6] py-5 text-sm tracking-[0.2em] uppercase font-semibold hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50"
            data-testid="place-order-button">
            {loading ? "Processing..." : paymentMethod === "razorpay" ? `Pay ₹${orderTotal.toLocaleString("en-IN")} — Place Order` : `Place Order · ₹${orderTotal.toLocaleString("en-IN")} COD`}
          </button>
          <p className="text-[11px] text-muted-foreground text-center mt-3 tracking-wide">Handcrafted in Delhi · Secure checkout</p>
        </div>
      </form>
      )}
    </div>
  );
}
